import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  ColumnDef,
  EmptyStateComponent,
  SettingsListHeader,
  SettingsLoadingPlaceholder,
  TagEditorComponent,
  TagEditorItem,
  TagFormDialog,
  TagFormResult,
  TfColumnDefDirective,
  TfDataTableComponent,
} from '@tourfold/libraries/shared-ui';
import { TagResponse } from '@tourfold/libraries/tf-api-client-public-v2';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { TagsService } from './tags.service';
import {
  resolveSettingsLoadErrorDetails,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';

@Component({
  selector: 'tf-settings-tags-list',
  imports: [
    CommonModule,
    ButtonModule,
    ConfirmDialogModule,
    TooltipModule,
    TranslateModule,
    EmptyStateComponent,
    SettingsListHeader,
    SettingsLoadingPlaceholder,
    TagEditorComponent,
    TfDataTableComponent,
    TfColumnDefDirective,
    TagFormDialog,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './tags-list.html',
  styleUrl: './tags-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class Tags {
  private readonly tagsService = inject(TagsService);
  private readonly translateService = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly resourceAccessService = inject(ResourceAccessService);
  private readonly searchSubject = new Subject<string>();

  readonly canCreateTag = computed(() => this.resourceAccessService.canPerform('tags', 'create'));
  readonly canUpdateTag = computed(() => this.resourceAccessService.canPerform('tags', 'update'));
  readonly canDeleteTag = computed(() => this.resourceAccessService.canPerform('tags', 'delete'));

  // Server doesn't yet support search/sort on /tags; tag count is small in practice,
  // so we load the full list once and filter client-side.
  private readonly allTags = signal<TagResponse[]>([]);

  readonly searchQuery = signal<string>('');
  readonly isLoading = signal(false);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly isSaving = signal(false);
  readonly isDialogVisible = signal(false);
  readonly editingTag = signal<TagResponse | null>(null);

  private readonly dialog = viewChild(TagFormDialog);

  readonly visibleTags = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) return this.allTags();
    return this.allTags().filter((t) => (t.name ?? '').toLowerCase().includes(query));
  });
  readonly showEmptyState = computed(
    () =>
      this.hasLoadedOnce() && this.searchQuery().trim() === '' && this.visibleTags().length === 0
  );

  readonly columns: ColumnDef[] = [
    { field: 'name', title: 'SETTINGS_TAGS.NAME', type: 'custom' },
    { field: '_actions', title: 'SETTINGS_TAGS.ACTIONS', type: 'custom', width: '6rem' },
  ];

  constructor() {
    this.setupSearchDebounce();
    this.loadTags();
  }

  private setupSearchDebounce() {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => this.searchQuery.set(query));
  }

  private loadTags(): void {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    this.actionError.set(null);
    this.tagsService.getTags().subscribe({
      next: (response) => {
        this.allTags.set(response.items);
        this.isLoading.set(false);
        this.loadError.set(null);
        this.loadErrorDetails.set(null);
        this.initialLoadFailed.set(false);
        this.hasLoadedOnce.set(true);
      },
      error: (error: unknown) => {
        this.isLoading.set(false);
        const message = this.translateService.instant('SETTINGS_TAGS.ERROR.TAG_LOAD_FAILED');
        if (hadLoadedOnce) {
          this.hasLoadedOnce.set(true);
          this.actionError.set(message);
        } else {
          this.loadError.set(message);
          this.initialLoadFailed.set(true);
          void this.updateLoadErrorDetails(error);
        }
      },
    });
  }

  reloadTags(): void {
    this.loadTags();
  }

  toEditorItems(tag: TagResponse): readonly TagEditorItem[] {
    return [{ id: tag.id ?? '', name: tag.name ?? '', color: tag.color ?? '' }];
  }

  onSearchChange(query: string): void {
    this.searchSubject.next(query);
  }

  createTag(): void {
    this.editingTag.set(null);
    this.isDialogVisible.set(true);
  }

  editTag(tag: TagResponse): void {
    this.editingTag.set(tag);
    this.isDialogVisible.set(true);
  }

  onDialogVisibilityChange(visible: boolean): void {
    if (!visible) {
      this.isDialogVisible.set(false);
      this.editingTag.set(null);
    }
  }

  onDialogCancel(): void {
    this.isDialogVisible.set(false);
    this.editingTag.set(null);
  }

  async onDialogSave(result: TagFormResult): Promise<void> {
    const editing = this.editingTag();
    this.isSaving.set(true);
    try {
      if (editing && editing.id) {
        await firstValueFrom(
          this.tagsService.updateTag(editing.id, {
            name: result.name,
            color: result.color,
            lockVersion: editing.lockVersion,
          })
        );
      } else {
        await firstValueFrom(
          this.tagsService.createTag({ name: result.name, color: result.color })
        );
      }
      this.isDialogVisible.set(false);
      this.editingTag.set(null);
      this.loadTags();
    } catch (error: unknown) {
      const applied = await this.dialog()?.applyServerError(error);
      // Field-level errors (e.g. a name uniqueness conflict) are shown inline inside the
      // dialog; only surface a banner behind the dialog for general/unmapped failures.
      if (applied && applied.fieldCount > 0) {
        if (applied.generalMessages.length > 0) {
          this.actionError.set(applied.generalMessages.join(' '));
        }
        return;
      }
      const joinedGeneral = applied?.generalMessages.join(' ') ?? '';
      const fallbackKey = editing
        ? 'SETTINGS_TAGS.ERROR.TAG_UPDATE_FAILED'
        : 'SETTINGS_TAGS.ERROR.TAG_CREATE_FAILED';
      const message =
        joinedGeneral === '' ? this.translateService.instant(fallbackKey) : joinedGeneral;
      this.actionError.set(message);
    } finally {
      this.isSaving.set(false);
    }
  }

  deleteTag(tag: TagResponse): void {
    const messageTemplate = this.translateService.instant(
      'SETTINGS_TAGS.DELETE_CONFIRM_MESSAGE'
    ) as string;
    const message = messageTemplate
      .replace('{name}', tag.name ?? '')
      .replace('{count}', String(tag.usageCount ?? 0));

    if (!tag.id) return;
    const tagId = tag.id;

    this.confirmationService.confirm({
      message,
      header: this.translateService.instant('SETTINGS_TAGS.DELETE_CONFIRM_TITLE'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.isLoading.set(true);
        this.tagsService.deleteTag(tagId).subscribe({
          next: () => {
            this.isLoading.set(false);
            this.loadTags();
          },
          error: () => {
            this.isLoading.set(false);
            this.actionError.set(
              this.translateService.instant('SETTINGS_TAGS.ERROR.TAG_DELETE_FAILED')
            );
          },
        });
      },
    });
  }

  clearActionError(): void {
    this.actionError.set(null);
  }

  private async updateLoadErrorDetails(error: unknown): Promise<void> {
    this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
  }
}
